import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Brain, Target, Award, Clock, CheckCircle2, AlertTriangle,
  ArrowRight, RotateCcw, HelpCircle, FileText, ChevronRight, Compass,
  BookOpen, Star, AlertCircle, Check, X, UserCheck, Scale, Users, Info
} from 'lucide-react';
import {
  PSICOMETRICO_QUESTIONS,
  PERSONALITY_INVENTORY,
  MOSS_SITUATIONAL_QUESTIONS,
  CONSEJOS_ENTREVISTA_UNINAV,
  UNINAV_MODULE_INFO,
  calculatePsychometricEvaluation,
  calculatePersonalityEvaluation,
  calculateMossEvaluation,
  generateComprehensiveVerdict
} from '../data/uninavAssessmentData';
import '../styles/CursosVortex.css';

export default function UninavAssessment() {
  const navigate = useNavigate();

  // Tab activo: 'hub' | 'personalidad' | 'psicometrico' | 'moss' | 'veredicto' | 'entrevista'
  const [activeTab, setActiveTab] = useState('hub');

  // ── ESTADO BATERÍA 1: PERSONALIDAD (MMPI / Escalas de Validez) ──
  const [persCurrentIdx, setPersCurrentIdx] = useState(0);
  const [persAnswers, setPersAnswers] = useState({});

  // ── ESTADO BATERÍA 2: PSICOMÉTRICO (Razonamiento / CI / Bennett) ──
  const [psiCurrentIdx, setPsiCurrentIdx] = useState(0);
  const [psiAnswers, setPsiAnswers] = useState({});
  const [psiTimeLeft, setPsiTimeLeft] = useState(60 * 60); // 60 minutos para 100 reactivos
  const [psiTimerActive, setPsiTimerActive] = useState(false);

  // ── ESTADO BATERÍA 3: MOSS (Juicio Situacional y Liderazgo) ──
  const [mossCurrentIdx, setMossCurrentIdx] = useState(0);
  const [mossAnswers, setMossAnswers] = useState({});

  // Temporizador psicométrico
  useEffect(() => {
    let interval = null;
    if (psiTimerActive && psiTimeLeft > 0) {
      interval = setInterval(() => {
        setPsiTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (psiTimeLeft === 0 && psiTimerActive) {
      setPsiTimerActive(false);
      setActiveTab('veredicto');
    }
    return () => clearInterval(interval);
  }, [psiTimerActive, psiTimeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Acciones de inicio
  const startPersonalidad = () => {
    setPersCurrentIdx(0);
    setActiveTab('personalidad');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startPsicometrico = () => {
    setPsiCurrentIdx(0);
    setPsiTimeLeft(60 * 60);
    setPsiTimerActive(true);
    setActiveTab('psicometrico');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startMoss = () => {
    setMossCurrentIdx(0);
    setActiveTab('moss');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Cálculos evaluativos
  const psychometricEval = calculatePsychometricEvaluation(PSICOMETRICO_QUESTIONS, psiAnswers);
  const personalityEval = calculatePersonalityEvaluation(PERSONALITY_INVENTORY, persAnswers);
  const mossEval = calculateMossEvaluation(MOSS_SITUATIONAL_QUESTIONS, mossAnswers);

  const globalVerdict = generateComprehensiveVerdict({
    psychometric: psychometricEval,
    personality: personalityEval,
    moss: mossEval
  });

  const totalAnsweredCount = Object.keys(persAnswers).length + Object.keys(psiAnswers).length + Object.keys(mossAnswers).length;
  const totalBatteryItems = PERSONALITY_INVENTORY.length + PSICOMETRICO_QUESTIONS.length + MOSS_SITUATIONAL_QUESTIONS.length;

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '24px 16px 80px',
      fontFamily: "'Inter', sans-serif",
      color: '#e2e8f0'
    }}>

      {/* ── HEADER TÁCTICO NAVAL ── */}
      <div style={{
        background: 'linear-gradient(135deg, #0b1a2e 0%, #06111f 100%)',
        border: '1px solid rgba(197, 160, 89, 0.35)',
        borderRadius: '16px',
        padding: '28px 24px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute', top: '-20px', right: '-20px', opacity: 0.08,
          fontSize: '12rem', userSelect: 'none', pointerEvents: 'none'
        }}>
          ⚓
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <span style={{
            fontSize: '0.68rem', fontFamily: 'monospace', fontWeight: 'bold',
            letterSpacing: '1px', textTransform: 'uppercase', color: '#c5a059',
            background: 'rgba(197, 160, 89, 0.15)', padding: '4px 10px', borderRadius: '50px',
            border: '1px solid rgba(197, 160, 89, 0.3)'
          }}>
            ⚓ Proceso de Selección UNINAV 2026 · SEMAR
          </span>
          <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Batería Integral de Tamizaje</span>
        </div>

        <h1 style={{
          fontSize: '1.8rem', fontWeight: '800', margin: '0 0 8px',
          color: '#ffffff', letterSpacing: '-0.5px'
        }}>
          Simulador Psicométrico, Perfil Clínico y Liderazgo Naval
        </h1>
        <p style={{
          fontSize: '0.88rem', color: '#94a3b8', margin: 0, maxWidth: '800px', lineHeight: 1.5
        }}>
          En la Universidad Naval (HENM, Médico y Enfermería Naval) no se aplica un solo examen aislado, sino una <strong>batería de pruebas</strong> que incluye inventarios de personalidad con <strong>escalas de sinceridad</strong>, razonamiento con tiempo límite y toma de decisiones tácticas.
        </p>

        {/* Barra de progreso global */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '16px', marginTop: '20px',
          paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.08)'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#cbd5e1', marginBottom: '6px' }}>
              <span>Reactivos Respondidos: <strong>{totalAnsweredCount}</strong> de {totalBatteryItems}</span>
              <span style={{ color: '#c5a059', fontWeight: 'bold' }}>{Math.round((totalAnsweredCount / totalBatteryItems) * 100)}% de la Batería</span>
            </div>
            <div style={{ width: '100%', height: '8px', background: 'rgba(0,0,0,0.4)', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                width: `${(totalAnsweredCount / totalBatteryItems) * 100}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #38bdf8 0%, #c5a059 100%)',
                borderRadius: '4px',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
          <button
            onClick={() => setActiveTab('veredicto')}
            style={{
              background: 'linear-gradient(135deg, #c5a059 0%, #9a7b38 100%)',
              border: 'none', borderRadius: '8px', padding: '8px 16px',
              color: '#0a1625', fontWeight: 'bold', fontSize: '0.8rem',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px'
            }}
          >
            <Award size={15} /> Ver Veredicto
          </button>
        </div>
      </div>

      {/* ── SELECTOR DE TABS TÁCTICOS ── */}
      <div style={{
        display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap',
        background: '#071526', padding: '6px', borderRadius: '12px', border: '1px solid #1a2d42'
      }}>
        {[
          { id: 'hub', label: 'Tablero General', icon: <Compass size={16} /> },
          { id: 'personalidad', label: `1. Personalidad (${Object.keys(persAnswers).length}/${PERSONALITY_INVENTORY.length})`, icon: <Scale size={16} /> },
          { id: 'psicometrico', label: `2. Psicométrico (${Object.keys(psiAnswers).length}/${PSICOMETRICO_QUESTIONS.length})`, icon: <Brain size={16} /> },
          { id: 'moss', label: `3. Liderazgo MOSS (${Object.keys(mossAnswers).length}/${MOSS_SITUATIONAL_QUESTIONS.length})`, icon: <Users size={16} /> },
          { id: 'veredicto', label: 'Dictamen Oficial SEMAR', icon: <Award size={16} /> },
          { id: 'entrevista', label: 'Guía Fase Definitiva', icon: <Shield size={16} /> },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            style={{
              flex: '1 1 auto',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '10px 14px', borderRadius: '8px', fontSize: '0.82rem', fontWeight: '600',
              border: 'none', cursor: 'pointer', transition: 'all 0.2s',
              background: activeTab === tab.id ? 'linear-gradient(135deg, #102a45 0%, #0d2238 100%)' : 'transparent',
              color: activeTab === tab.id ? '#c5a059' : '#94a3b8',
              boxShadow: activeTab === tab.id ? '0 2px 8px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(197, 160, 89, 0.4)' : 'none'
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ─────────────────────────────────────────────────────────────
          TAB 1: TABLERO GENERAL (HUB)
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'hub' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
          
          {/* Card Batería 1: Personalidad */}
          <div style={{
            background: '#091c30', border: '1px solid rgba(56, 189, 248, 0.25)',
            borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                background: 'rgba(56, 189, 248, 0.15)', border: '1px solid rgba(56, 189, 248, 0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#38bdf8'
              }}>
                <Scale size={20} />
              </div>
              <div>
                <span style={{ fontSize: '0.7rem', color: '#38bdf8', fontWeight: 'bold', textTransform: 'uppercase' }}>Batería 1 · Estabilidad</span>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#fff' }}>Personalidad y Perfil Clínico</h3>
              </div>
            </div>
            <p style={{ fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.5, flex: 1 }}>
              Basado en inventarios tipo MMPI/16PF adaptados a SEMAR. Evalúa madurez emocional, subordinación y control de impulsos.
            </p>
            <div style={{
              background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)',
              borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '0.75rem', color: '#fca5a5'
            }}>
              <strong>⚠️ Incluye Escalas de Sinceridad (L):</strong> Detecta si falseas respuestas pretendiendo ser perfecto o si incurres en contradicciones.
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{PERSONALITY_INVENTORY.length} reactivos Verdadero/Falso</span>
              <span style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: 'bold' }}>
                {Object.keys(persAnswers).length} completados
              </span>
            </div>
            <button
              onClick={startPersonalidad}
              style={{
                width: '100%', background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                border: 'none', borderRadius: '10px', padding: '12px', color: '#fff',
                fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
              }}
            >
              Realizar Test de Personalidad <ArrowRight size={16} />
            </button>
          </div>

          {/* Card Batería 2: Psicométrico */}
          <div style={{
            background: '#091c30', border: '1px solid rgba(197, 160, 89, 0.25)',
            borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                background: 'rgba(197, 160, 89, 0.15)', border: '1px solid rgba(197, 160, 89, 0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c5a059'
              }}>
                <Brain size={20} />
              </div>
              <div>
                <span style={{ fontSize: '0.7rem', color: '#c5a059', fontWeight: 'bold', textTransform: 'uppercase' }}>Batería 2 · Cognitivo</span>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#fff' }}>Razonamiento e Inteligencia (CI)</h3>
              </div>
            </div>
            <p style={{ fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.5, flex: 1 }}>
              Matrices tipo Raven, aptitud mecánica Bennett (poleas, engranes, palancas), vistas 3D de cubos y cálculo de cinemática náutica bajo cronómetro.
            </p>
            <div style={{
              background: 'rgba(197, 160, 89, 0.08)', border: '1px solid rgba(197, 160, 89, 0.25)',
              borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '0.75rem', color: '#fde68a'
            }}>
              <strong>⏱️ Tiempo Oficial: 60 minutos:</strong> Mide tu velocidad de procesamiento y resolución bajo estrés cronometrado.
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{PSICOMETRICO_QUESTIONS.length} reactivos técnicos</span>
              <span style={{ fontSize: '0.75rem', color: '#c5a059', fontWeight: 'bold' }}>
                CI Estimado: {psychometricEval.estimatedCI}
              </span>
            </div>
            <button
              onClick={startPsicometrico}
              style={{
                width: '100%', background: 'linear-gradient(135deg, #c5a059 0%, #9a7b38 100%)',
                border: 'none', borderRadius: '10px', padding: '12px', color: '#091c30',
                fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
              }}
            >
              Iniciar Test Psicométrico (60 min) <ArrowRight size={16} />
            </button>
          </div>

          {/* Card Batería 3: Test MOSS */}
          <div style={{
            background: '#091c30', border: '1px solid rgba(52, 211, 153, 0.25)',
            borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#34d399'
              }}>
                <Users size={20} />
              </div>
              <div>
                <span style={{ fontSize: '0.7rem', color: '#34d399', fontWeight: 'bold', textTransform: 'uppercase' }}>Batería 3 · Liderazgo</span>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#fff' }}>Juicio Situacional (Test MOSS)</h3>
              </div>
            </div>
            <p style={{ fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.5, flex: 1 }}>
              Escenarios tácticos de mando naval, relaciones interpersonales, zafarranchos de averías y resolución de conflictos entre cadetes.
            </p>
            <div style={{
              background: 'rgba(52, 211, 153, 0.08)', border: '1px solid rgba(52, 211, 153, 0.25)',
              borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', fontSize: '0.75rem', color: '#a7f3d0'
            }}>
              <strong>⚓ Ética y Disciplina Naval:</strong> Determina tu capacidad de mando asertivo y tacto humano sin recurrir a la debilidad ni al abuso.
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{MOSS_SITUATIONAL_QUESTIONS.length} casos de estudio</span>
              <span style={{ fontSize: '0.75rem', color: '#34d399', fontWeight: 'bold' }}>
                {Object.keys(mossAnswers).length} contestados
              </span>
            </div>
            <button
              onClick={startMoss}
              style={{
                width: '100%', background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                border: 'none', borderRadius: '10px', padding: '12px', color: '#fff',
                fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
              }}
            >
              Iniciar Test MOSS de Liderazgo <ArrowRight size={16} />
            </button>
          </div>

        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 2: BATERÍA 1 - PERSONALIDAD Y ESCALAS DE SINCERIDAD
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'personalidad' && (
        <div style={{ maxWidth: '850px', margin: '0 auto' }}>
          
          <div style={{
            background: '#091c30', border: '1px solid rgba(56, 189, 248, 0.3)',
            borderRadius: '16px', padding: '24px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
          }}>
            {/* Cabecera del test */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: 'bold', textTransform: 'uppercase' }}>
                Inventario de Personalidad SEMAR · Reactivo {persCurrentIdx + 1} de {PERSONALITY_INVENTORY.length}
              </span>
              <span style={{
                background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8',
                padding: '4px 10px', borderRadius: '50px', fontSize: '0.72rem', fontWeight: 'bold'
              }}>
                Dimensión: {PERSONALITY_INVENTORY[persCurrentIdx].dimension}
              </span>
            </div>

            {/* Enunciado */}
            <div style={{
              background: '#061322', border: '1px solid #1a2f47',
              borderRadius: '12px', padding: '24px', marginBottom: '24px', minHeight: '110px',
              display: 'flex', alignItems: 'center'
            }}>
              <p style={{ fontSize: '1.15rem', color: '#f8fafc', margin: 0, lineHeight: 1.5, fontWeight: '500' }}>
                "{PERSONALITY_INVENTORY[persCurrentIdx].statement}"
              </p>
            </div>

            {/* Opciones V / F */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              {[
                { val: 'V', label: 'Verdadero (V)', desc: 'Describe fielmente mi conducta o pensamiento habitual', color: '#34d399' },
                { val: 'F', label: 'Falso (F)', desc: 'No coincide con mi comportamiento o actitud real', color: '#f87171' }
              ].map((opt) => {
                const isSelected = persAnswers[PERSONALITY_INVENTORY[persCurrentIdx].id] === opt.val;
                return (
                  <button
                    key={opt.val}
                    onClick={() => {
                      setPersAnswers({
                        ...persAnswers,
                        [PERSONALITY_INVENTORY[persCurrentIdx].id]: opt.val
                      });
                      if (persCurrentIdx < PERSONALITY_INVENTORY.length - 1) {
                        setTimeout(() => setPersCurrentIdx((prev) => prev + 1), 200);
                      }
                    }}
                    style={{
                      background: isSelected ? 'rgba(56, 189, 248, 0.15)' : '#071526',
                      border: `2px solid ${isSelected ? opt.color : '#1a2f47'}`,
                      borderRadius: '12px', padding: '16px', cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: '6px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '1.05rem', fontWeight: 'bold', color: isSelected ? opt.color : '#fff' }}>
                        {opt.label}
                      </span>
                      {isSelected && <Check size={18} color={opt.color} />}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                      {opt.desc}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Navegación y progreso */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '16px', borderTop: '1px solid #1a2f47' }}>
              <button
                disabled={persCurrentIdx === 0}
                onClick={() => setPersCurrentIdx((prev) => prev - 1)}
                style={{
                  background: 'transparent', border: '1px solid #243c58',
                  borderRadius: '8px', padding: '8px 16px', color: '#94a3b8',
                  cursor: persCurrentIdx === 0 ? 'not-allowed' : 'pointer', fontSize: '0.8rem'
                }}
              >
                Anterior
              </button>

              <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', maxWidth: '300px', padding: '4px' }}>
                {PERSONALITY_INVENTORY.map((_, idx) => (
                  <div
                    key={idx}
                    onClick={() => setPersCurrentIdx(idx)}
                    style={{
                      width: '10px', height: '10px', borderRadius: '50%', cursor: 'pointer',
                      background: persAnswers[PERSONALITY_INVENTORY[idx].id] ? '#38bdf8' : (idx === persCurrentIdx ? '#fff' : '#1e3857')
                    }}
                  />
                ))}
              </div>

              {persCurrentIdx < PERSONALITY_INVENTORY.length - 1 ? (
                <button
                  onClick={() => setPersCurrentIdx((prev) => prev + 1)}
                  style={{
                    background: '#0284c7', border: 'none',
                    borderRadius: '8px', padding: '8px 20px', color: '#fff',
                    fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem'
                  }}
                >
                  Siguiente
                </button>
              ) : (
                <button
                  onClick={() => setActiveTab('veredicto')}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none', borderRadius: '8px', padding: '8px 20px', color: '#fff',
                    fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem'
                  }}
                >
                  Finalizar Inventario
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 3: BATERÍA 2 - TEST PSICOMÉTRICO CRONOMETRADO
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'psicometrico' && (
        <div style={{ maxWidth: '850px', margin: '0 auto' }}>
          
          <div style={{
            background: '#091c30', border: '1px solid rgba(197, 160, 89, 0.3)',
            borderRadius: '16px', padding: '24px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
          }}>
            {/* Header del psicométrico con temporizador */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#c5a059', fontWeight: 'bold', textTransform: 'uppercase' }}>
                  Reactivo {psiCurrentIdx + 1} de {PSICOMETRICO_QUESTIONS.length}
                </span>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                  {PSICOMETRICO_QUESTIONS[psiCurrentIdx].category} · {PSICOMETRICO_QUESTIONS[psiCurrentIdx].battery}
                </div>
              </div>

              {/* Temporizador */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                background: psiTimeLeft < 300 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(0,0,0,0.4)',
                border: `1px solid ${psiTimeLeft < 300 ? '#ef4444' : 'rgba(197, 160, 89, 0.4)'}`,
                borderRadius: '8px', padding: '6px 14px', color: psiTimeLeft < 300 ? '#f87171' : '#c5a059',
                fontFamily: 'monospace', fontWeight: 'bold', fontSize: '0.95rem'
              }}>
                <Clock size={16} />
                <span>{formatTime(psiTimeLeft)}</span>
              </div>
            </div>

            {/* Enunciado */}
            <div style={{
              background: '#061322', border: '1px solid #1a2f47',
              borderRadius: '12px', padding: '20px', marginBottom: '20px'
            }}>
              <p style={{ fontSize: '1.05rem', color: '#fff', margin: 0, lineHeight: 1.5 }}>
                {PSICOMETRICO_QUESTIONS[psiCurrentIdx].question}
              </p>
            </div>

            {/* Opciones */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              {PSICOMETRICO_QUESTIONS[psiCurrentIdx].options.map((opt, optIdx) => {
                const isSelected = psiAnswers[psiCurrentIdx] === optIdx;
                return (
                  <button
                    key={optIdx}
                    onClick={() => {
                      setPsiAnswers({ ...psiAnswers, [psiCurrentIdx]: optIdx });
                    }}
                    style={{
                      background: isSelected ? 'rgba(197, 160, 89, 0.15)' : '#071526',
                      border: `1.5px solid ${isSelected ? '#c5a059' : '#1a2f47'}`,
                      borderRadius: '10px', padding: '14px 18px', textAlign: 'left',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
                      color: isSelected ? '#c5a059' : '#e2e8f0', fontSize: '0.9rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{
                      width: '26px', height: '26px', borderRadius: '50%',
                      background: isSelected ? '#c5a059' : 'rgba(255,255,255,0.06)',
                      color: isSelected ? '#091c30' : '#94a3b8', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.8rem',
                      flexShrink: 0
                    }}>
                      {String.fromCharCode(65 + optIdx)}
                    </span>
                    <span style={{ flex: 1 }}>{opt}</span>
                    {isSelected && <Check size={18} color="#c5a059" />}
                  </button>
                );
              })}
            </div>

            {/* Navegación */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '16px', borderTop: '1px solid #1a2f47' }}>
              <button
                disabled={psiCurrentIdx === 0}
                onClick={() => setPsiCurrentIdx((prev) => prev - 1)}
                style={{
                  background: 'transparent', border: '1px solid #243c58',
                  borderRadius: '8px', padding: '8px 16px', color: '#94a3b8',
                  cursor: psiCurrentIdx === 0 ? 'not-allowed' : 'pointer', fontSize: '0.8rem'
                }}
              >
                Anterior
              </button>

              <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                Respondidas: {Object.keys(psiAnswers).length} de {PSICOMETRICO_QUESTIONS.length}
              </span>

              {psiCurrentIdx < PSICOMETRICO_QUESTIONS.length - 1 ? (
                <button
                  onClick={() => setPsiCurrentIdx((prev) => prev + 1)}
                  style={{
                    background: '#c5a059', border: 'none',
                    borderRadius: '8px', padding: '8px 20px', color: '#091c30',
                    fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem'
                  }}
                >
                  Siguiente
                </button>
              ) : (
                <button
                  onClick={() => {
                    setPsiTimerActive(false);
                    setActiveTab('veredicto');
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none', borderRadius: '8px', padding: '8px 20px', color: '#fff',
                    fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem'
                  }}
                >
                  Entregar y Ver Veredicto
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 4: BATERÍA 3 - TEST MOSS DE LIDERAZGO SITUACIONAL
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'moss' && (
        <div style={{ maxWidth: '850px', margin: '0 auto' }}>
          
          <div style={{
            background: '#091c30', border: '1px solid rgba(52, 211, 153, 0.3)',
            borderRadius: '16px', padding: '24px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
          }}>
            {/* Header del test MOSS */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '0.75rem', color: '#34d399', fontWeight: 'bold', textTransform: 'uppercase' }}>
                Test MOSS Militar · Escenario {mossCurrentIdx + 1} de {MOSS_SITUATIONAL_QUESTIONS.length}
              </span>
              <span style={{
                background: 'rgba(52, 211, 153, 0.15)', color: '#34d399',
                padding: '4px 10px', borderRadius: '50px', fontSize: '0.72rem', fontWeight: 'bold'
              }}>
                {MOSS_SITUATIONAL_QUESTIONS[mossCurrentIdx].dimension}
              </span>
            </div>

            {/* Escenario táctico */}
            <div style={{
              background: '#061322', border: '1px solid #1a2f47',
              borderRadius: '12px', padding: '20px', marginBottom: '20px'
            }}>
              <p style={{ fontSize: '1.05rem', color: '#fff', margin: 0, lineHeight: 1.5, fontWeight: '500' }}>
                {MOSS_SITUATIONAL_QUESTIONS[mossCurrentIdx].scenario}
              </p>
            </div>

            {/* Opciones situacionales */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
              {MOSS_SITUATIONAL_QUESTIONS[mossCurrentIdx].options.map((opt, optIdx) => {
                const isSelected = mossAnswers[MOSS_SITUATIONAL_QUESTIONS[mossCurrentIdx].id] === optIdx;
                return (
                  <button
                    key={optIdx}
                    onClick={() => {
                      setMossAnswers({
                        ...mossAnswers,
                        [MOSS_SITUATIONAL_QUESTIONS[mossCurrentIdx].id]: optIdx
                      });
                    }}
                    style={{
                      background: isSelected ? 'rgba(52, 211, 153, 0.15)' : '#071526',
                      border: `1.5px solid ${isSelected ? '#34d399' : '#1a2f47'}`,
                      borderRadius: '10px', padding: '14px 18px', textAlign: 'left',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
                      color: isSelected ? '#34d399' : '#e2e8f0', fontSize: '0.88rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{
                      width: '26px', height: '26px', borderRadius: '50%',
                      background: isSelected ? '#34d399' : 'rgba(255,255,255,0.06)',
                      color: isSelected ? '#091c30' : '#94a3b8', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '0.8rem',
                      flexShrink: 0
                    }}>
                      {String.fromCharCode(65 + optIdx)}
                    </span>
                    <span style={{ flex: 1 }}>{opt.text}</span>
                    {isSelected && <Check size={18} color="#34d399" />}
                  </button>
                );
              })}
            </div>

            {/* Navegación */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '16px', borderTop: '1px solid #1a2f47' }}>
              <button
                disabled={mossCurrentIdx === 0}
                onClick={() => setMossCurrentIdx((prev) => prev - 1)}
                style={{
                  background: 'transparent', border: '1px solid #243c58',
                  borderRadius: '8px', padding: '8px 16px', color: '#94a3b8',
                  cursor: mossCurrentIdx === 0 ? 'not-allowed' : 'pointer', fontSize: '0.8rem'
                }}
              >
                Anterior
              </button>

              <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                Contestados: {Object.keys(mossAnswers).length} de {MOSS_SITUATIONAL_QUESTIONS.length}
              </span>

              {mossCurrentIdx < MOSS_SITUATIONAL_QUESTIONS.length - 1 ? (
                <button
                  onClick={() => setMossCurrentIdx((prev) => prev + 1)}
                  style={{
                    background: '#059669', border: 'none',
                    borderRadius: '8px', padding: '8px 20px', color: '#fff',
                    fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem'
                  }}
                >
                  Siguiente
                </button>
              ) : (
                <button
                  onClick={() => setActiveTab('veredicto')}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none', borderRadius: '8px', padding: '8px 20px', color: '#fff',
                    fontWeight: 'bold', cursor: 'pointer', fontSize: '0.8rem'
                  }}
                >
                  Finalizar MOSS y Ver Veredicto
                </button>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 5: VEREDICTO Y DICTAMEN OFICIAL SEMAR (APTO / NO APTO)
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'veredicto' && (
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          
          {/* Tarjeta de Veredicto Principal */}
          <div style={{
            background: 'linear-gradient(135deg, #091c30 0%, #06111f 100%)',
            border: `2px solid ${globalVerdict.color}`,
            borderRadius: '16px', padding: '32px 24px', marginBottom: '24px',
            boxShadow: `0 8px 32px ${globalVerdict.color}22`, textAlign: 'center'
          }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '6px 16px', borderRadius: '50px',
              background: `${globalVerdict.color}22`, border: `1px solid ${globalVerdict.color}`,
              color: globalVerdict.color, fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '16px'
            }}>
              {globalVerdict.badgeText}
            </div>

            <h2 style={{ fontSize: '2rem', margin: '0 0 12px', color: '#fff' }}>
              Veredicto de Admisión Naval: <span style={{ color: globalVerdict.color }}>{globalVerdict.verdict}</span>
            </h2>

            <p style={{ fontSize: '0.95rem', color: '#cbd5e1', maxWidth: '750px', margin: '0 auto 20px', lineHeight: 1.6 }}>
              {globalVerdict.primaryReason}
            </p>

            <div style={{
              background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '12px', padding: '16px', maxWidth: '700px', margin: '0 auto', textAlign: 'left',
              display: 'flex', gap: '12px', alignItems: 'flex-start'
            }}>
              <Info size={20} color={globalVerdict.color} style={{ flexShrink: 0, marginTop: '2px' }} />
              <div style={{ fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.5 }}>
                <strong style={{ color: '#fff' }}>Dictamen de la Comisión Psicológica:</strong> {globalVerdict.advice}
              </div>
            </div>
          </div>

          {/* Desglose de las 3 Baterías */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            
            {/* Desglose Personalidad y Escalas de Sinceridad */}
            <div style={{
              background: '#091c30', border: `1px solid ${personalityEval.validityColor}`,
              borderRadius: '14px', padding: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <Scale size={20} color={personalityEval.validityColor} />
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#fff' }}>Auditoría de Sinceridad</h4>
              </div>
              <div style={{ fontSize: '0.78rem', color: personalityEval.validityColor, fontWeight: 'bold', marginBottom: '8px' }}>
                {personalityEval.validityStatus}
              </div>
              <p style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.4, margin: '0 0 12px' }}>
                {personalityEval.validitySummary}
              </p>
              <div style={{ borderTop: '1px solid #1a2f47', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                <span style={{ color: '#64748b' }}>Escala L (Mentira):</span>
                <span style={{ color: personalityEval.hasLieFlag ? '#ef4444' : '#34d399', fontWeight: 'bold' }}>
                  {personalityEval.lieTriggers} alertas detectadas
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginTop: '4px' }}>
                <span style={{ color: '#64748b' }}>Pares Inconsistentes:</span>
                <span style={{ color: personalityEval.hasInconsistencyFlag ? '#ef4444' : '#34d399', fontWeight: 'bold' }}>
                  {personalityEval.contradictions} contradicciones
                </span>
              </div>
            </div>

            {/* Desglose Psicométrico */}
            <div style={{
              background: '#091c30', border: `1px solid ${psychometricEval.isPassing ? '#10b981' : '#ef4444'}`,
              borderRadius: '14px', padding: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <Brain size={20} color={psychometricEval.isPassing ? '#10b981' : '#ef4444'} />
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#fff' }}>Inteligencia y CI Naval</h4>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '1.8rem', fontWeight: '800', color: '#c5a059', fontFamily: 'monospace' }}>
                  {psychometricEval.estimatedCI}
                </span>
                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>CI Estimado (Mínimo: 90)</span>
              </div>
              <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '12px' }}>
                Percentil Nacional: <strong>{psychometricEval.percentile}%</strong>
              </div>
              <div style={{ borderTop: '1px solid #1a2f47', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                <span style={{ color: '#64748b' }}>Aciertos Totales:</span>
                <span style={{ color: '#fff', fontWeight: 'bold' }}>
                  {psychometricEval.correctCount} / {psychometricEval.totalQuestions} ({psychometricEval.percentage}%)
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginTop: '4px' }}>
                <span style={{ color: '#64748b' }}>Umbral Cognitivo:</span>
                <span style={{ color: psychometricEval.isPassing ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>
                  {psychometricEval.isPassing ? 'Superado' : 'No Superado'}
                </span>
              </div>
            </div>

            {/* Desglose Liderazgo MOSS */}
            <div style={{
              background: '#091c30', border: `1px solid ${mossEval.isPassing ? '#34d399' : '#f59e0b'}`,
              borderRadius: '14px', padding: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <Users size={20} color={mossEval.isPassing ? '#34d399' : '#f59e0b'} />
                <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#fff' }}>Liderazgo Táctico (MOSS)</h4>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '1.8rem', fontWeight: '800', color: '#34d399', fontFamily: 'monospace' }}>
                  {mossEval.percentage}%
                </span>
                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Afinidad de Mando</span>
              </div>
              <p style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.4, margin: '0 0 12px' }}>
                {mossEval.isPassing ? 'Capacidad para supervisar y tomar decisiones justas sin perder la disciplina.' : 'Requiere afianzar el tacto interpersonal y la serenidad de mando.'}
              </p>
              <div style={{ borderTop: '1px solid #1a2f47', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                <span style={{ color: '#64748b' }}>Puntaje Obtenido:</span>
                <span style={{ color: '#fff', fontWeight: 'bold' }}>
                  {mossEval.totalScore} / {mossEval.maxPossible} pts
                </span>
              </div>
            </div>

          </div>

          {/* Botones de acción */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setActiveTab('entrevista')}
              style={{
                background: 'linear-gradient(135deg, #c5a059 0%, #9a7b38 100%)',
                border: 'none', borderRadius: '10px', padding: '12px 24px',
                color: '#091c30', fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}
            >
              <Shield size={16} /> Preparar Entrevista en Antón Lizardo
            </button>
            <button
              onClick={() => {
                setPersAnswers({});
                setPsiAnswers({});
                setMossAnswers({});
                setActiveTab('hub');
              }}
              style={{
                background: 'transparent', border: '1px solid #243c58',
                borderRadius: '10px', padding: '12px 24px', color: '#94a3b8',
                fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'
              }}
            >
              <RotateCcw size={16} /> Reiniciar Toda la Batería
            </button>
          </div>

        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 6: GUÍA ESTRATÉGICA DE ENTREVISTA EN ANTÓN LIZARDO
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'entrevista' && (
        <div style={{ maxWidth: '850px', margin: '0 auto' }}>
          
          <div style={{
            background: '#091c30', border: '1px solid rgba(197, 160, 89, 0.3)',
            borderRadius: '16px', padding: '28px', marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '12px',
                background: 'rgba(197, 160, 89, 0.15)', border: '1px solid #c5a059',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c5a059'
              }}>
                <Shield size={24} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#fff' }}>
                  Directrices Oficiales para la Entrevista Psicológica Presencial
                </h3>
                <span style={{ fontSize: '0.78rem', color: '#c5a059' }}>
                  Heroica Escuela Naval Militar · Antón Lizardo, Veracruz
                </span>
              </div>
            </div>

            <p style={{ fontSize: '0.88rem', color: '#cbd5e1', lineHeight: 1.6, marginBottom: '24px' }}>
              La entrevista psicológica individual no es una plática informal. Es una exploración exhaustiva donde un oficial psicólogo militar valida las respuestas que diste en la batería psicométrica, analiza tu congruencia y pone a prueba tu estabilidad emocional.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {CONSEJOS_ENTREVISTA_UNINAV.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    background: '#061322', border: '1px solid #1a2f47',
                    borderRadius: '12px', padding: '18px'
                  }}
                >
                  <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem', color: '#c5a059' }}>
                    {item.title}
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.5 }}>
                    {item.tip}
                  </p>
                </div>
              ))}
            </div>

            <div style={{ textAlign: 'center', marginTop: '28px' }}>
              <button
                onClick={() => setActiveTab('veredicto')}
                style={{
                  background: 'linear-gradient(135deg, #c5a059 0%, #9a7b38 100%)',
                  border: 'none', borderRadius: '10px', padding: '12px 24px',
                  color: '#091c30', fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer'
                }}
              >
                Volver al Veredicto
              </button>
            </div>
          </div>

        </div>
      )}

    </div>
  );
}
